import { useDeviceInfo } from '@core/shared';
import { SlideType } from '@core/swagger-client';
import { VideoLayout } from '@core/types';
import { FullScreenButton } from '@core/ui';
import { setActiveSlide, useAppDispatch } from '@online-webinar/store';
import { throttle } from 'lodash-es';
import { useCallback, useEffect, useState, useTransition } from 'react';
import { useTranslation } from 'react-i18next';
import { useSwipeable } from 'react-swipeable';
import type { SlidesProps } from '../../../types';
import { ImageSlider } from '../../atoms/ImageSlider';
import { BroadcasterPresentationSlideNavigator } from '../../molecules/BroadcasterPresentationSlideNavigator';
import { NextSlidePreview } from '../../molecules/NextSlidePreview';
import { PresentationSlideNavigator } from '../../molecules/PresentationSlideNavigator';
import { SwitchFullSizeButton } from '../../molecules/SwitchFullSizeButton';

export const PresentationSlides = ({ slides, currentSlideNumber, classNames, showFullScreen, isBroadcaster, isController, isMobile, isPreview, videoLayout }: SlidesProps) => {
    const [, startTransition] = useTransition();
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [localCurrentSlideNumber, setLocalCurrentSlideNumber] = useState<number>(currentSlideNumber);
    const [fullScreen, setFullScreen] = useState<boolean>(false);
    const totalSlides = slides.length;

    const { operatingSystemInformation } = useDeviceInfo();
    const { isIOS } = operatingSystemInformation;

    const swipeConfiguration = {
        delta: 10, // min distance(px) before a swipe starts
        preventScrollOnSwipe: false, // prevents scroll during swipe
        trackTouch: true, // track touch input
        trackMouse: false, // track mouse input
        rotationAngle: 0, // set a rotation angle
        swipeDuration: 250, // allowable duration of a swipe (ms).
        touchEventOptions: { passive: true } // options for touch listeners
    };
    const swipeHandlers = useSwipeable({
        onSwipedRight: () => {
            if (localCurrentSlideNumber > 1) {
                startTransition(goToPrevSlide);
            }
        },
        onSwipedLeft: () => {
            if (localCurrentSlideNumber < currentSlideNumber) {
                startTransition(goToNextSlide);
            }
        },
        ...swipeConfiguration
    });

    useEffect(() => {
        setLocalCurrentSlideNumber(currentSlideNumber);
    }, [currentSlideNumber]);

    const handleEscapeButton = useCallback(
        (event: KeyboardEvent) => {
            if (event.code === 'Escape' && fullScreen) {
                setFullScreen(false);
            }
        },
        [fullScreen]
    );

    useEffect(() => {
        if (!fullScreen) {
            return;
        }

        document.addEventListener('keydown', handleEscapeButton);
        return () => {
            document.removeEventListener('keydown', handleEscapeButton);
        };
    }, [fullScreen, handleEscapeButton]);

    const changeSlide = (slideNumber: number) => {
        if (isBroadcaster || isController) {
            dispatch(setActiveSlide(slideNumber));
        }
    };

    const slideToDisplay = localCurrentSlideNumber >= 0 && localCurrentSlideNumber < slides.length ? slides[localCurrentSlideNumber] : undefined;
    const slideToPreview = localCurrentSlideNumber >= 0 && localCurrentSlideNumber + 1 < slides.length ? slides[localCurrentSlideNumber + 1] : undefined;

    const goToNextSlide = useCallback(() => {
        if (localCurrentSlideNumber === totalSlides - 1) {
            return;
        }

        setLocalCurrentSlideNumber((prevSlideNumber) => {
            const newSlideNumber = prevSlideNumber + 1;
            changeSlide(newSlideNumber);
            return newSlideNumber;
        });
    }, [localCurrentSlideNumber, totalSlides]);

    const goToPrevSlide = useCallback(() => {
        if (localCurrentSlideNumber !== 0) {
            setLocalCurrentSlideNumber((prevSlideNumber) => {
                const newSlideNumber = prevSlideNumber - 1;
                changeSlide(newSlideNumber);
                return newSlideNumber;
            });
        }
    }, [localCurrentSlideNumber, totalSlides]);

    const goToCurrentSlide = () => {
        setLocalCurrentSlideNumber(currentSlideNumber);
        changeSlide(currentSlideNumber);
    };

    const goToLastSlide = () => {
        setLocalCurrentSlideNumber(totalSlides - 1);
        changeSlide(totalSlides - 1);
    };

    const goToFirstSlide = () => {
        setLocalCurrentSlideNumber(0);
        changeSlide(0);
    };

    const handleSlideChange = useCallback(
        throttle((event: KeyboardEvent) => {
            // Check if there is no Tablist that currently has focus, so we don't navigate between tabs and change slides at the same time.
            // Also check if an input or textarea element has focus
            const activeElement = document.activeElement;
            const tabList = document.querySelector('[role="tablist"]');
            if (
                (tabList && tabList.contains(document.activeElement)) ||
                (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA'))
            ) {
                return;
            }

            const { code } = event;
            switch (code) {
                case 'ArrowRight':
                case 'PageUp':
                    goToNextSlide();
                    break;
                case 'ArrowLeft':
                case 'PageDown':
                    goToPrevSlide();
                    break;
                default:
                    break;
            }
        }, 200),
        [goToNextSlide, goToPrevSlide]
    );

    useEffect(() => {
        if (!isBroadcaster && !isController && !isPreview) {
            return;
        }

        document.addEventListener('keyup', handleSlideChange);
        return () => {
            document.removeEventListener('keyup', handleSlideChange);
        };
    }, [handleSlideChange, isBroadcaster, isController, isPreview]);

    // TODO: Turn into a composite component that render any kind of navigator/ content.

    return (
        <div className={`${fullScreen ? 'fixed h-full w-full inset-0 bg-white flex z-50' : (isMobile ? 'relative h-full bg-white' : 'h-full max-w-full aspect-video m-auto')}`}>
            <div
                className={`group relative h-full ${fullScreen ? 'max-w-[1280px] max-h-[720px]' : 'max-h-full'} flex items-start justify-center m-auto overflow-hidden`}
            >
                {slideToDisplay && (
                    <>
                        <div
                            className={`h-full ${videoLayout === VideoLayout.PresentationWithVideoLarge && !fullScreen ? 'relative bc:w-1/4 bc:h-auto bc:ml-auto bc:mt-auto bc:mb-4 bc:mr-4 bc:z-1' : ''} ${videoLayout === VideoLayout.DesktopSharing ? 'bc:hidden' : ''}`}
                        >
                            {showFullScreen !== 'hidden' && (
                                <div className="absolute top-2 right-2 z-1">
                                    <FullScreenButton onClick={() => setFullScreen(!fullScreen)} isMaximized={fullScreen} />
                                </div>
                            )}
                            {slideToDisplay.type === SlideType.Image && (
                                <ImageSlider
                                    swipeHandlers={!isIOS ? swipeHandlers : undefined}
                                    activeSlideNumber={localCurrentSlideNumber}
                                    slideToDisplay={slideToDisplay}
                                    totalSlides={totalSlides}
                                    classNames={classNames}
                                />
                            )}
                        </div>
                        {!isController && (
                            isBroadcaster || isPreview ? (
                                <div
                                    className={`${isBroadcaster && 'absolute bottom-0 w-full flex items-end justify-between z-1'} 
                                                 ${isPreview && 'menu:absolute menu:bottom-0 menu:left-1/2 menu:transform menu:-translate-x-1/2'}`}
                                >
                                    {isPreview ?? (
                                        <div className="flex-1 flex justify-start">
                                            {/*<SwitchFullSizeButton />*/}
                                        </div>
                                    )}
                                    <BroadcasterPresentationSlideNavigator
                                        totalSlides={totalSlides}
                                        localCurrentSlideNumber={localCurrentSlideNumber}
                                        goToPrevSlide={goToPrevSlide}
                                        goToNextSlide={goToNextSlide}
                                    />
                                    {isPreview ?? (
                                        <div className="flex-1 flex justify-end">{slideToPreview && <NextSlidePreview slideToPreview={slideToPreview} />}</div>
                                    )}
                                </div>
                            ) : (
                                <div className="absolute inset-0 pointer-events-none">
                                    <PresentationSlideNavigator
                                        totalSlides={totalSlides}
                                        localCurrentSlideNumber={localCurrentSlideNumber}
                                        currentSlideNumber={currentSlideNumber}
                                        goToPrevSlide={goToPrevSlide}
                                        goToNextSlide={goToNextSlide}
                                        goToCurrentSlide={goToCurrentSlide}
                                        goToLastSlide={goToLastSlide}
                                        goToFirstSlide={goToFirstSlide}
                                    />
                                </div>
                            )
                        )}
                    </>
                )}
            </div>
        </div>
    );
};
